/* eslint-disable space-before-function-paren */
import _ from 'lodash';
import lunr from 'lunr';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import AuthService from '../../services/AuthService';
import Search from '../Search';
import { pageTypes } from '../../util/page';
import { LIST_ITEM_DELIMITER } from '../../util/form';
import { lunrEscapeQuery } from '../../util/search';

class PageSearch extends Component {
  constructor(props) {
    super(props);
    this.state = {
      suggestions: [],
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.loadSuggestions = this.loadSuggestions.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
    this.shouldRenderSuggestions = this.shouldRenderSuggestions.bind(this);
  }

  componentDidMount() {
    this.createIndex();
  }

  componentDidUpdate(prevProps) {
    const { user, branch } = this.props;
    if (user !== prevProps.user || branch !== prevProps.branch) {
      this.createIndex();
    }
  }

  onBlur(suggestion) {
    if (suggestion) {
      return this.props.onBlur(this.props.getSuggestionValue(suggestion));
    }

    return this.props.onBlur(suggestion);
  }

  onSuggestionSelected(event, { suggestion }) {
    if (suggestion === undefined) {
      return this.props.onSuggestionSelected('', '');
    }

    if (this.props.useHistory) {
      this.addHistoryItem(suggestion);
    }

    return this.props.onSuggestionSelected(this.props.getSuggestionValue(suggestion), suggestion);
  }

  onSuggestionsClearRequested() {
    this.setState({ suggestions: [] });
  }

  getHistory() {
    return JSON.parse(localStorage.getItem('searchHistory')) || [];
  }

  createIndex() {
    const { searchData, user, branch } = this.props;

    if (searchData === 'Unauthorized') {
      return;
    }

    const docs = [];
    if (user.id && branch.id) {
      for (const key of Object.keys(searchData)) {
        // Only show pages user has access to
        if (this.props.showAuthOnly && !AuthService.hasPermission(key)) {
          continue;
        }

        const doc = searchData[key];
        if (!this.props.showHidden && doc.hidden) {
          continue;
        }
        doc.name = key;
        docs.push(doc);
      }
    }

    this.index = lunr(function () {
      this.pipeline.remove(lunr.stopWordFilter);
      this.pipeline.remove(lunr.stemmer);
      this.searchPipeline.remove(lunr.stopWordFilter);
      this.searchPipeline.remove(lunr.stemmer);

      this.field('name', { boost: 10 });
      this.field('title');
      this.field('type');
      this.ref('name');

      docs.forEach(doc => {
        this.add(doc);
      });
    });
  }

  handleSubmit(value) {
    if (!this.props.highlightFirstSuggestion) {
      this.props.onSuggestionSelected(value, value);
    }
  }

  shouldRenderSuggestions(value) {
    if (!value && this.state.suggestions) {
      return true;
    }

    return value.trim().length > 0;
  }

  addHistoryItem(value) {
    if (!value) {
      return false;
    }
    const searchHistory = JSON.parse(localStorage.getItem('searchHistory')) || [];
    const MAX_SIZE = 5;
    const index = _.findIndex(searchHistory, suggestion => suggestion.name === value.name);

    // Already exists in search history
    if (index !== -1) {
      searchHistory.splice(index, 1);
    }

    if (searchHistory.length === MAX_SIZE) {
      searchHistory.pop();
    }

    value.history = true;
    searchHistory.unshift(value);
    localStorage.setItem('searchHistory', JSON.stringify(searchHistory));
    return true;
  }

  loadHistory() {
    const history = this.getHistory();
    const suggestions = [];

    history.forEach(item => {
      if (AuthService.hasPermission(item.name)) {
        suggestions.push(item);
      }
    });

    this.setState({ suggestions });
    return true;
  }

  loadSuggestions(value) {
    const { filter, searchData, useHistory, limit } = this.props;

    if (!value && useHistory) {
      this.loadHistory();
      return;
    }

    if (value === '?') {
      this.setState({ suggestions: filter(_.values(searchData).slice(0, 50)) });
      return;
    }

    const results = this.index.search(`${lunrEscapeQuery(value)}*`).slice(0, limit);

    const suggestions = [];
    const refMap = new Map();

    results.forEach(result => {
      refMap.set(result.ref, true);
      suggestions.push(searchData[result.ref]);
    });

    if (useHistory) {
      this.getHistory().forEach(item => {
        try {
          if (!refMap.has(item.name) && new RegExp(`${value}*`).test(item.name)) {
            suggestions.push(item);
          }
        } catch (e) {
          // bad regex
        }
      });
    }

    this.setState({ suggestions: filter(suggestions) });
  }

  renderSuggestion(suggestion) {
    let title = null;
    let icon = <i className="zmdi zmdi-file-text zmdi-hc-2x zmdi-hc-fw" />;

    if (suggestion.title) {
      title = (
        <span>
          <br />
          {suggestion.title}
        </span>
      );
    }

    const pageType = pageTypes[suggestion.type];
    if (pageType) {
      icon = <i title={pageType.title} className={`zmdi zmdi-${pageType.icon} zmdi-hc-2x zmdi-hc-fw`} />;

      if (suggestion.history) {
        icon = <i title={pageType.title} className="zmdi zmdi-time-restore zmdi-hc-2x zmdi-hc-fw" />;
      }
    }

    return (
      <table height="50" cellPadding="0" cellSpacing="0">
        <tbody>
          <tr>
            <td className="icon-cell">{icon}</td>
            <td className="page-cell">
              <b>{suggestion.name}</b>
              {title}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  render() {
    return (
      <Search
        {...this.props}
        debounce={0}
        loadSuggestions={this.loadSuggestions}
        onBlur={this.onBlur}
        onSubmit={this.handleSubmit}
        onSuggestionSelected={this.onSuggestionSelected}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
        renderSuggestion={this.renderSuggestion}
        shouldRenderSuggestions={this.shouldRenderSuggestions}
        suggestions={this.state.suggestions}
      />
    );
  }
}

PageSearch.defaultProps = {
  filter: suggestions => suggestions,
  getSuggestionValue: suggestion => `${suggestion.name}:${LIST_ITEM_DELIMITER}${suggestion.title}`,
  highlightFirstSuggestion: true,
  limit: 25,
  onBlur: v => v,
  onChange: v => v,
  placeholder: 'Page Search',
  showAuthOnly: true,
  selectOnBlur: true,
  showHidden: false,
  useHistory: false,
};

PageSearch.propTypes = {
  branch: PropTypes.object,
  controlId: PropTypes.string,
  filter: PropTypes.func,
  getSuggestionValue: PropTypes.func,
  highlightFirstSuggestion: PropTypes.bool,
  limit: PropTypes.number,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onSuggestionSelected: PropTypes.func,
  placeholder: PropTypes.string,
  renderSuggestionsContainer: PropTypes.func,
  showAuthOnly: PropTypes.bool,
  searchData: PropTypes.object,
  selectOnBlur: PropTypes.bool,
  showHidden: PropTypes.bool,
  useHistory: PropTypes.bool,
  user: PropTypes.object,
  value: PropTypes.any,
};

function mapStateToProps(state) {
  return {
    searchData: state.app.searchData,
    user: state.app.user,
    branch: state.app.branch,
  };
}

export default connect(mapStateToProps)(PageSearch);
